import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import Swal from 'sweetalert2';

import { Link } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import { collection, addDoc } from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';

import Navbar from '../../components/navbar/navbar';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';

const auth = getAuth();

const schema = Yup.object().shape({
  nombre: Yup.string().required('El nombre es obligatorio'),
  correo: Yup.string().email('Correo inválido').required('El correo es obligatorio'),
  contraseña: Yup.string()
    .min(10, 'Mínimo 10 caracteres')
    .matches(/[a-z]/, 'Debe incluir una minúscula')
    .matches(/[A-Z]/, 'Debe incluir una mayúscula')
    .matches(/\d/, 'Debe incluir un número')
    .matches(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, 'Debe incluir un carácter especial')
    .required('La contraseña es obligatoria'),
  telefono: Yup.string()
    .matches(/^\d{10}$/, 'Debe ser un número de 10 dígitos')
    .required('El teléfono es obligatorio'),
  tipo: Yup.string().oneOf(['asociado', 'asesor', 'admin']).required()
});

export default function CreateAccount() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  });

  const onSubmit = async (data: any) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        data.correo,
        data.contraseña
      );

      const user = userCredential.user;

      await addDoc(collection(db, 'usuarios'), {
        uid: user.uid,
        nombre: data.nombre,
        correo: data.correo,
        telefono: data.telefono,
        tipo: data.tipo
      });

      Swal.fire({
        icon: 'success',
        title: '¡Éxito!',
        text: 'Usuario registrado correctamente',
        confirmButtonColor: '#3085d6'
      });
      reset();
    } catch (error: any) {
      console.error('Error al crear usuario:', error.message);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message,
        confirmButtonColor: '#d33'
      });
    }
  };

  return (
    <>
      <Navbar transparent={false} />

      <div className="page-title">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 col-md-12">
              <h2 className="ipt-title">Crear Cuenta</h2>
              <span className="ipn-subtitle">Regístrate en la plataforma</span>
            </div>
          </div>
        </div>
      </div>

      <section className="gray-simple">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-7 col-lg-8 col-md-9">
              <div className="card border-0 rounded-4 p-xl-4 p-lg-4 p-md-4 p-3">
                <div className="simple-form">
                  <div className="form-header text-center mb-5">
                    <h4 className="fs-2">Registro de Usuario</h4>
                  </div>

                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row">
                      <div className="col-lg-6 col-md-6 mb-3">
                        <label className="mb-2">Nombre completo</label>
                        <input
                          {...register('nombre')}
                          className="form-control"
                          placeholder="Nombre completo"
                        />
                        {errors.nombre && <p className="text-danger mt-1">{errors.nombre.message}</p>}
                      </div>

                      <div className="col-lg-6 col-md-6 mb-3">
                        <label className="mb-2">Correo electrónico</label>
                        <input
                          type="email"
                          {...register('correo')}
                          className="form-control"
                          placeholder="Correo electrónico"
                        />
                        {errors.correo && <p className="text-danger mt-1">{errors.correo.message}</p>}
                      </div>

                      <div className="col-lg-6 col-md-6 mb-3">
                        <label className="mb-2">Contraseña</label>
                        <input
                          type="password"
                          {...register('contraseña')}
                          className="form-control"
                          placeholder="*******"
                        />
                        <small className="text-muted d-block mt-1">
                          Mínimo 10 caracteres. Debe incluir mayúscula, minúscula, número y carácter especial.
                        </small>
                        {errors.contraseña && <p className="text-danger mt-1">{errors.contraseña.message}</p>}
                      </div>

                      <div className="col-lg-6 col-md-6 mb-3">
                        <label className="mb-2">Teléfono</label>
                        <input
                          type="tel"
                          {...register('telefono')}
                          className="form-control"
                          placeholder="7711234567"
                        />
                        {errors.telefono && <p className="text-danger mt-1">{errors.telefono.message}</p>}
                      </div>

                      <div className="col-lg-6 col-md-6 mb-3">
                        <label className="mb-2">Tipo de usuario</label>
                        <select {...register('tipo')} className="form-control">
                          <option value="asociado">Asociado</option>
                          <option value="asesor">Asesor</option>
                          <option value="admin">Administrador</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-group">
                      <button
                        type="submit"
                        className="btn btn-primary full-width fw-medium"
                      >
                        Crear cuenta <i className="fa-solid fa-arrow-right-long ms-2"></i>
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
